import { ChangeDetectionStrategy, Component, input, Input } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AssessmentRowComponent } from '../assessment-row/assessment-row.component';
import { EmrInputComponent } from "../../../../shared/emr-input/emr-input.component";
import { EmrCheckboxComponent } from "../../../../shared/emr-checkbox/emr-checkbox.component";
import { WnlManagerService } from '../wnl-manager.service';

import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { FormOption } from 'src/app/shared/models/form-option.model';
import { GastrointestinalFormGroup } from 'src/app/calvin-emr-shared/models/assessments/gastrointestinal.model';

@Component({
  selector: 'app-gastrointestinal-form',
  templateUrl: './gastrointestinal-form.component.html',
  styleUrls: ['./gastrointestinal-form.component.scss', '../assessments-tab.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    AssessmentRowComponent,
    EmrInputComponent,
    EmrCheckboxComponent,
  ],
})
export class GastrointestinalFormComponent {

  formGroup = input.required<GastrointestinalFormGroup>();
  patient = input.required<Patient>();
  wnlManager = new WnlManagerService();

  // Options for DatePicker
  minDate: Date;
  maxDate: Date;

  bowelSoundsOptions = [
    new FormOption('Hypoactive'),
    new FormOption('Hyperactive'),
    new FormOption('Absent'),
    new FormOption('WNL'),
  ];

  emesisOptions = [
    new FormOption('Small'),
    new FormOption('Medium'),
    new FormOption('Large'),
    new FormOption('WNL'),
  ];

  stomaSiteOptions = [
    new FormOption('Pink'),
    new FormOption('Beefy red'),
    new FormOption('Pale'),
    new FormOption('Purple'),
    new FormOption('None'),
  ];

  outputOptions = [
    new FormOption('Loose'),
    new FormOption('Watery'),
    new FormOption('Formed'),
  ];

  oralDietOptions = [
    new FormOption('Total Feed'),
    new FormOption('Partial Assist'),
    new FormOption('Tray set up'),
    new FormOption('Independent'),
  ];

  weightLossOptions = [
    new FormOption('< 5%'),
    new FormOption('5%-10%'),
    new FormOption('> 10%'),
  ]

  constructor() {
    const currentYear = new Date().getFullYear();
    this.minDate = new Date(currentYear - 20, 0, 1);
    this.maxDate = new Date();
    this.reset();
  }

  ngOnInit(): void {
    this.formGroup().controls.tubeFeed.valueChanges.subscribe(() => {
      if (!this.formGroup().controls.tubeFeed.value) {
        this.formGroup().controls.tubeType.setValue('');
        this.formGroup().controls.bagChangedHours.setValue('');
        this.formGroup().controls.siteAssessment.setValue('');
      }
    });

    this.formGroup().controls.reducedAppetite.valueChanges.subscribe(() => {
      this.formGroup().controls.nutritionScore.setValue(`${this.nutritionScore}`);
    });

    this.formGroup().controls.weightLossPercentage.valueChanges.subscribe(() => {
      this.formGroup().controls.nutritionScore.setValue(`${this.nutritionScore}`);
    });
  }

  reset() {
    this.wnlManager.setControls(
      [
        { control: 'ruq', default: 'WNL', wnl: false },
        { control: 'rlq', default: 'WNL', wnl: false },
        { control: 'luq', default: 'WNL', wnl: false },
        { control: 'llq', default: 'WNL', wnl: false },
        { control: 'nausea', default: false, wnl: false },
        { control: 'emesis', default: 'WNL', wnl: false },
        { control: 'incontinence', default: false, wnl: false },
        { control: 'stomaSite', default: 'None', wnl: false },
        { control: 'output', default: 'Formed', wnl: false },
      ]
    );
  }

  get bowelSoundsDefault() {
    return this.wnlManager.controlColl['luq'].default;
  }

  get nauseaDefault() {
    return this.wnlManager.controlColl['nausea'].default;
  }

  get emesisDefault() {
    return this.wnlManager.controlColl['emesis'].default;
  }

  get bowelControlDefault() {
    return this.wnlManager.controlColl['bowelControl'].default;
  }

  get stomaSiteDefault() {
    return this.wnlManager.controlColl['stomaSite'].default;
  }

  get outputDefault() {
    return this.wnlManager.controlColl['output'].default;
  }

  get BMIScore() {
    const BMIScore = this.patient().getBMI();
    if (BMIScore > 20) {
      return 0;
    } else if (BMIScore > 18.5) {
      return 1;
    } else {
      return 3;
    }
  }

  get weightScore() {
    switch (this.formGroup().controls.weightLossPercentage.value) {
      case '> 10%':
        return 2;
      case '5%-10%':
        return 1;
      default:
        return 0
    }
  }

  get acuteDiseaseEffectScore() {
    return this.formGroup().controls.reducedAppetite.value ? 1 : 0;
  }

  get nutritionScore() {
    return (
      this.BMIScore +
      this.weightScore +
      this.acuteDiseaseEffectScore
    );
  }

  get nutritionRiskCategory(): string {
    const nutritionScore = this.nutritionScore;
    if (nutritionScore >= 2) {
      return 'High';
    } else if (nutritionScore === 1) {
      return 'Medium';
    } else {
      return 'Low';
    }
  }
}